import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import styles from './styles.scss';
import DropDown from '../../atoms/dropdowns/dropdown';
import Datalist from '../../atoms/datalist';
import { isType } from '../../../utils/react-children-utils/react-children-utils';

class DatePicker extends React.Component {
    static getMonths() {
        return [
            'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December',
        ];
    }

    static getMonthIndex(month) {
        return DatePicker.getMonths().indexOf(month) + 1;
    }

    static getThirtyMonth() {
        return [2, 4, 6, 9, 11];
    }

    static leapYear(year) {
        return ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
    }

    static fixDay(year, month, day) {
        return ((month === 2 && DatePicker.leapYear(year) && day > 29) // Leap Year
            || (month === 2 && !DatePicker.leapYear(year) && day > 28) // Normal February
            || (DatePicker.getThirtyMonth().includes(month) && day > 30)); // Months with 30 days
    }

    static parseDefaultValue(defaultValue) {
        try {
            const dt = DateTime.fromISO(defaultValue);

            return {
                day: dt.day,
                month: dt.month,
                year: dt.year,
                value: dt.toISODate(),
            };
        } catch {
            return '';
        }
    }

    constructor(props) {
        super(props);

        this.setDate = this.setDate.bind(this);
        this.getYears = this.getYears.bind(this);
        this.validateDay = this.validateDay.bind(this);
        this.validateMonth = this.validateMonth.bind(this);
        this.validateYear = this.validateYear.bind(this);

        this.state = {
            day: 0,
            month: 0,
            year: 0,
            date: '',
        };
    }

    componentDidMount() {
        const { validation } = this.props;

        this.input.addEventListener('change', validation, false);
    }

    static getDays(year, month) {
        let numberOfDays = 31;

        if (month === 2) {
            numberOfDays = 28;
            if (year > 0 && DatePicker.leapYear(year)) {
                numberOfDays = 29;
            }
        } else if (DatePicker.getThirtyMonth().includes(month)) {
            numberOfDays = 30;
        }

        const days = [];
        let current = 1;
        while (current <= numberOfDays) {
            days.push(`${current}`);
            current += 1;
        }

        return days;
    }

    getYears() {
        const { maxyear, minyear } = this.props;
        let current = maxyear;

        const years = [];

        while (current >= minyear) {
            years.push(`${current}`);
            current -= 1;
        }

        return years;
    }

    setDate(day, month, year) {
        // Write input only if all values are present
        if (year) {
            let date = '';
            let newDay = day;
            if (day && month && year) {
                if (DatePicker.fixDay(year, month, day)) {
                    newDay = 1;
                    date = DateTime.local(year, month, newDay).toISODate();
                } else {
                    date = DateTime.local(year, month, day).toISODate();
                }
            }

            this.setState(() => ({
                year,
                month,
                day: newDay,
                date,
            }), () => this.input.dispatchEvent(new Event('change')));
            // explicitly dispatch event as onchange/oninput does not
            // catch changes made programmatically
        }
    }

    validateDay(event) {
        const { value } = event.target;

        if (value) {
            const day = parseInt(value, 10);

            const month = DatePicker.getMonthIndex(this.month.value);
            const year = parseInt(this.year.value, 10);

            this.setDate(day, month, year);
        }
    }

    validateMonth(event) {
        const { value } = event.target;

        if (value) {
            const day = parseInt(this.day.value, 10);
            const month = DatePicker.getMonthIndex(value);
            const year = parseInt(this.year.value, 10);

            this.setDate(day, month, year);
        }
    }

    validateYear(event) {
        const { value } = event.target;
        const { minyear, validation } = this.props;

        const day = parseInt(this.day.value, 10);
        const month = DatePicker.getMonthIndex(this.month.value);

        const year = parseInt(value, 10);

        if (year < minyear || year > DateTime.local().year) {
            validation(event, 'Please enter a valid year');
        } else {
            this.setDate(day, month, year);
        }
    }

    render() {
        let {
            date,
            day,
            month,
            year,
        } = this.state;
        const {
            children,
            name,
            invalidclass,
            validation,
            minyear,
            maxyear,
            defaultvalue,
        } = this.props;

        // get the input and set the defaultValue if present
        const input = React.Children.toArray(children).find((c) => isType(c, 'input'));
        const inputPropsDefaultvalue = input?.props?.defaultValue ?? '';

        const defaultDate = DatePicker.parseDefaultValue(inputPropsDefaultvalue === '' ? defaultvalue : inputPropsDefaultvalue);
        const monthDefault = DatePicker.getMonths()[defaultDate.month - 1]; // change to index
        if (!date && defaultDate.value) {
            day = defaultDate.day;
            month = defaultDate.month;
            year = defaultDate.year;
            date = defaultDate.value;
        }

        // Refresh the dates to appear in the dropdown
        const days = DatePicker.getDays(year, month);

        return (
            <div className={`columns is-desktop ${styles.datepicker}`}>
                <div className={`column ${styles['no-side-padding']} ${styles['small-right-padding']}`}>
                    <DropDown inputRef={(ref) => { this.day = ref; }} options={days} name="day" defaultValue={defaultDate.day || day || ''} placeholder="Day" validation={this.validateDay} invalidclass={invalidclass} />
                </div>
                <div className={`column ${styles['no-side-padding']} ${styles['small-right-padding']}`}>
                    <DropDown inputRef={(ref) => { this.month = ref; }} options={DatePicker.getMonths()} name="month" defaultValue={monthDefault || ''} placeholder="Month" validation={this.validateMonth} invalidclass={invalidclass} />
                </div>
                <span className={`column ${styles.year} ${styles['no-side-padding']}`}>
                    <Datalist inputType="number" min={minyear} max={maxyear} options={this.getYears()} inputRef={(ref) => { this.year = ref; }} name="year" placeholder="Year" defaultValue={defaultDate.year || '1990'} validation={this.validateYear} invalidclass={invalidclass} />
                </span>
                <input name={name} type="hidden" ref={(ref) => { this.input = ref; }} onChange={validation} value={date} data-testid={`${name}-datepicker`} />
            </div>
        );
    }
}

DatePicker.propTypes = {
    children: PropTypes.node.isRequired,
    name: PropTypes.string.isRequired,
    maxyear: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]).isRequired,
    minyear: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]).isRequired,
    validation: PropTypes.func,
    invalidclass: PropTypes.string,
    defaultvalue: PropTypes.string,
};

DatePicker.defaultProps = {
    validation: () => { },
    invalidclass: '',
    defaultvalue: '',
};

export default DatePicker;
